import type { TicketStatus, TicketPriority } from '../types';

/** Formats a numeric ticket ID with leading zeros (e.g. 1 → "0000001") */
export function formatTicketId(numericId: number): string {
  return String(numericId).padStart(7, '0');
}

export const STATUS_LABELS: Record<TicketStatus, string> = {
  OPEN: 'Deschis',
  ENCORSA_ASSIGNING: 'Alocare',
  ENCORSA_ESTIMATING: 'Estimare',
  ENCORSA_IN_PROGRESS: 'În lucru',
  ENCORSA_SUPERVISOR_REVIEW: 'Review supervizor',
  WAITING_CLIENT: 'La client pt. clarificări',
  RESOLVED: 'Rezolvat, Așteptăm Feedback',
  CLOSED: 'Închis',
};

export const STATUS_COLORS: Record<TicketStatus, string> = {
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
  ENCORSA_ASSIGNING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
  ENCORSA_ESTIMATING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300',
  ENCORSA_IN_PROGRESS: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/50 dark:text-cyan-300',
  ENCORSA_SUPERVISOR_REVIEW: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-300',
  WAITING_CLIENT: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
  RESOLVED: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
  CLOSED: 'bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-300',
};

export const PRIORITY_LABELS: Record<TicketPriority, string> = {
  ESTETIC: 'Estetic',
  NORMAL: 'Normal',
  CRITIC: 'Critic',
  BUG: 'BUG (garan\u021bie)',
  CHANGE_REQUEST: 'Modificare func\u021bionalitate',
};

export const PRIORITY_COLORS: Record<TicketPriority, string> = {
  ESTETIC: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  NORMAL: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  CRITIC: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  BUG: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  CHANGE_REQUEST: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
};

/** Solid chart colors per priority — must match the tag colors in PRIORITY_COLORS (CRITIC = red, BUG = orange, NORMAL = blue, ESTETIC = slate, CHANGE_REQUEST = purple) */
export const PRIORITY_CHART_COLORS: Record<TicketPriority, { light: string; dark: string }> = {
  CRITIC: { light: '#dc2626', dark: '#f87171' },
  BUG: { light: '#ea580c', dark: '#fb923c' },
  NORMAL: { light: '#2563eb', dark: '#60a5fa' },
  ESTETIC: { light: '#64748b', dark: '#94a3b8' },
  CHANGE_REQUEST: { light: '#7c3aed', dark: '#a78bfa' },
};

/** Workflow order of statuses — used to sort charts on the x-axis (flow order, not alphabetical) */
export const STATUS_FLOW_ORDER: TicketStatus[] = [
  'OPEN', 'ENCORSA_ASSIGNING', 'ENCORSA_ESTIMATING', 'ENCORSA_IN_PROGRESS',
  'ENCORSA_SUPERVISOR_REVIEW', 'WAITING_CLIENT', 'RESOLVED', 'CLOSED',
];

export const PRIORITY_DESCRIPTIONS: Record<TicketPriority, string> = {
  CRITIC: 'Aplicația sau o funcționalitate esențială nu poate fi utilizată.',
  NORMAL: 'O funcționalitate nu funcționează corect, dar aplicația poate fi utilizată în continuare.',
  ESTETIC: 'Probleme de afișare sau text care nu afectează funcționarea aplicației.',
  BUG: 'Eroare de funcționare care produce rezultate incorecte sau comportament neașteptat.',
  CHANGE_REQUEST: 'Cerere de modificare a unei funcționalități existente sau adăugare funcționalitate nouă.',
};

export const ALL_STATUSES: TicketStatus[] = [
  'OPEN', 'ENCORSA_ASSIGNING', 'ENCORSA_ESTIMATING',
  'ENCORSA_IN_PROGRESS', 'ENCORSA_SUPERVISOR_REVIEW',
  'WAITING_CLIENT', 'RESOLVED', 'CLOSED',
];

export const ALL_PRIORITIES: TicketPriority[] = ['ESTETIC', 'NORMAL', 'CRITIC', 'BUG', 'CHANGE_REQUEST'];

/** Get status label, role-aware: clients see "La mine pt. clarificări" for WAITING_CLIENT */
export function getStatusLabel(status: TicketStatus, role?: string): string {
  if (status === 'WAITING_CLIENT' && role === 'CLIENT') return 'La mine pt. clarificări';
  return STATUS_LABELS[status];
}

/** Workflow transitions shown as action buttons in admin detail page */
export const WORKFLOW_TRANSITIONS: Record<string, { action: string; label: string; variant: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' }[]> = {
  ENCORSA_ASSIGNING: [
    { action: 'assign', label: 'Alocă la agent', variant: 'primary' },
  ],
  ENCORSA_ESTIMATING: [
    { action: 'start_work', label: 'Pune în lucru', variant: 'primary' },
  ],
  ENCORSA_IN_PROGRESS: [
    { action: 'review', label: 'Trimite la review', variant: 'primary' },
  ],
  ENCORSA_SUPERVISOR_REVIEW: [
    { action: 'approve', label: 'Aprobă', variant: 'primary' },
    { action: 'rework', label: 'Trimite înapoi', variant: 'outline' },
  ],
  WAITING_CLIENT: [
    { action: 'client_responded', label: 'Clientul a răspuns', variant: 'primary' },
  ],
  RESOLVED: [
    { action: 'reopen', label: 'Redeschide', variant: 'outline' },
  ],
};
